import React, { useEffect, useState } from "react";
import { useAppData } from "./store";
import { X, Save } from "lucide-react";
import { toast } from "sonner";

const TIPOS_ATIVO = ["Ação", "FII"];
const TIPOS_CDI   = ["CDB","LCI","LCA","RDB","Tesouro","Debênture","Outro"];

export const CRYPTO_LIST = [
  { moeda: "Bitcoin",   symbol: "BTC",  geckoId: "bitcoin"       },
  { moeda: "Ethereum",  symbol: "ETH",  geckoId: "ethereum"      },
  { moeda: "BNB",       symbol: "BNB",  geckoId: "binancecoin"   },
  { moeda: "Solana",    symbol: "SOL",  geckoId: "solana"        },
  { moeda: "Cardano",   symbol: "ADA",  geckoId: "cardano"       },
  { moeda: "Ripple",    symbol: "XRP",  geckoId: "ripple"        },
  { moeda: "Dogecoin",  symbol: "DOGE", geckoId: "dogecoin"      },
  { moeda: "Polkadot",  symbol: "DOT",  geckoId: "polkadot"      },
  { moeda: "Chainlink", symbol: "LINK", geckoId: "chainlink"     },
  { moeda: "USDT",      symbol: "USDT", geckoId: "tether"        },
  { moeda: "Outro",     symbol: "",     geckoId: ""              },
];

function ModalShell({ title, subtitle, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div>
            <div className="font-display font-bold text-lg">{title}</div>
            {subtitle && <div className="text-xs text-slate-400 mt-0.5">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/40";
const sel = "w-full h-10 px-3 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none";
const lbl = "text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1.5";

// ─── Ação / FII Modal ─────────────────────────────────────────
export const AcaoModal = ({ open, onClose, item, tipo: tipoForcado }) => {
  const { addAcao, updateAcao } = useAppData();
  const EMPTY = {
    ticker: "", tipo: tipoForcado || "Ação",
    qtd: "", pmc: "",
    dataCompra: new Date().toISOString().slice(0, 10),
    dividendosManuais: "",
  };
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    setForm(item
      ? {
          ...item,
          qtd: String(item.qtd),
          pmc: String(item.pmc),
          dataCompra: item.dataCompra || new Date().toISOString().slice(0, 10),
          dividendosManuais: String(item.dividendosManuais ?? item.dividendos ?? ""),
        }
      : { ...EMPTY, tipo: tipoForcado || "Ação" });
  }, [open, item]); // eslint-disable-line

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const n   = (v) => Number(String(v).replace(",", "."));

  const submit = (e) => {
    e.preventDefault();
    const ticker = form.ticker.trim().toUpperCase();
    if (!ticker)         return toast.error("Informe o ticker.");
    if (!form.dataCompra) return toast.error("Informe a data de compra.");
    const qtd = n(form.qtd);
    const pmc = n(form.pmc);
    if (qtd <= 0) return toast.error("Quantidade inválida.");
    if (pmc <= 0) return toast.error("PMC inválido.");
    const payload = {
      ticker, tipo: form.tipo, qtd, pmc,
      dataCompra: form.dataCompra,
      dividendosManuais: n(form.dividendosManuais) || 0,
    };
    if (item) { updateAcao(item.ticker, payload); toast.success("Ativo atualizado!"); }
    else      { addAcao(payload);                  toast.success("Ativo adicionado!"); }
    onClose();
  };

  const isLabel = form.tipo === "FII" ? "Rendimentos manuais (R$)" : "Dividendos manuais (R$)";

  if (!open) return null;
  return (
    <ModalShell
      title={item ? "Editar ativo" : "Novo ativo"}
      subtitle="Cotação e dividendos buscados automaticamente"
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        {/* Tipo — só ao criar */}
        {!item && (
          <div>
            <label className={lbl}>Tipo</label>
            <div className="flex gap-2">
              {TIPOS_ATIVO.map((t) => (
                <button key={t} type="button" onClick={() => set({ tipo: t })}
                  className={`flex-1 h-9 rounded-xl text-sm font-semibold border transition ${
                    form.tipo === t
                      ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-300"
                      : "bg-slate-800 border-slate-700 text-slate-400"
                  }`}
                >{t}</button>
              ))}
            </div>
          </div>
        )}

        {/* Ticker */}
        <div>
          <label className={lbl}>Ticker</label>
          <input
            value={form.ticker}
            onChange={(e) => set({ ticker: e.target.value.toUpperCase() })}
            placeholder={form.tipo === "FII" ? "MXRF11" : "PETR4"}
            disabled={!!item}
            className={inp}
            data-testid="acao-ticker"
          />
        </div>

        {/* QTD + PMC */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Quantidade</label>
            <input value={form.qtd} onChange={(e) => set({ qtd: e.target.value })} inputMode="numeric" placeholder="100" className={inp} />
          </div>
          <div>
            <label className={lbl}>Preço Médio (R$)</label>
            <input value={form.pmc} onChange={(e) => set({ pmc: e.target.value })} inputMode="decimal" placeholder="9,85" className={inp} />
          </div>
        </div>

        {/* Data de compra (required) + Dividendos manuais (optional) */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Data de compra <span className="text-rose-400">*</span></label>
            <input
              type="date"
              value={form.dataCompra}
              onChange={(e) => set({ dataCompra: e.target.value })}
              className={inp}
            />
          </div>
          <div>
            <label className={lbl}>{isLabel} <span className="text-slate-600">(opcional)</span></label>
            <input
              value={form.dividendosManuais}
              onChange={(e) => set({ dividendosManuais: e.target.value })}
              inputMode="decimal" placeholder="0,00"
              className={inp}
            />
          </div>
        </div>

        <p className="text-[10px] text-slate-500 -mt-1">
          Dividendos automáticos são buscados via BRAPI desde a data de compra.
        </p>

        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="flex-1 h-10 rounded-xl border border-slate-700 text-sm text-slate-300 hover:bg-slate-800 transition">Cancelar</button>
          <button type="submit" className="flex-1 h-10 rounded-xl monyx-btn-primary text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition">
            <Save className="h-4 w-4" /> Salvar
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

// ─── Crypto Modal ─────────────────────────────────────────────
export const CryptoModal = ({ open, onClose, item }) => {
  const { addCrypto, updateCrypto } = useAppData();
  const EMPTY = {
    geckoId: "bitcoin", moeda: "Bitcoin", symbol: "BTC",
    qtd: "", pmc: "",
    dataCompra: new Date().toISOString().slice(0, 10),
  };
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    setForm(item
      ? { ...item, qtd: String(item.qtd), pmc: String(item.pmc), dataCompra: item.dataCompra || new Date().toISOString().slice(0, 10) }
      : EMPTY
    );
  }, [open, item]); // eslint-disable-line

  const set  = (patch) => setForm((f) => ({ ...f, ...patch }));
  const n    = (v) => Number(String(v).replace(",", "."));

  const handleCryptoSelect = (e) => {
    const found = CRYPTO_LIST.find((c) => c.geckoId === e.target.value);
    if (found && found.geckoId) set({ geckoId: found.geckoId, moeda: found.moeda, symbol: found.symbol });
    else set({ geckoId: "", moeda: "", symbol: "" });
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.geckoId && !form.moeda) return toast.error("Selecione a criptomoeda.");
    if (!form.dataCompra) return toast.error("Informe a data de compra.");
    const qtd = n(form.qtd);
    const pmc = n(form.pmc);
    if (qtd <= 0) return toast.error("Quantidade inválida.");
    if (pmc <= 0) return toast.error("Preço médio inválido.");
    const payload = { geckoId: form.geckoId, moeda: form.moeda, symbol: form.symbol, qtd, pmc, dataCompra: form.dataCompra };
    if (item) { updateCrypto(item.id, payload); toast.success("Cripto atualizada!"); }
    else      { addCrypto(payload);              toast.success("Cripto adicionada!"); }
    onClose();
  };

  const isOutro = form.geckoId === "" || !CRYPTO_LIST.slice(0, -1).find((c) => c.geckoId === form.geckoId);

  if (!open) return null;
  return (
    <ModalShell title={item ? "Editar cripto" : "Nova criptomoeda"} subtitle="Cotação via Coinbase · atualiza a cada 60s" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={lbl}>Criptomoeda</label>
          <select value={form.geckoId || "outro"} onChange={handleCryptoSelect} className={sel}>
            {CRYPTO_LIST.map((c) => (
              <option key={c.geckoId || "outro"} value={c.geckoId || "outro"}>
                {c.moeda}{c.symbol ? ` (${c.symbol})` : ""}
              </option>
            ))}
          </select>
        </div>
        {isOutro && (
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className={lbl}>Nome</label>
              <input value={form.moeda} onChange={(e) => set({ moeda: e.target.value })} placeholder="Ex: Avalanche" className={inp} />
            </div>
            <div>
              <label className={lbl}>Símbolo</label>
              <input value={form.symbol} onChange={(e) => set({ symbol: e.target.value.toUpperCase() })} placeholder="AVAX" className={inp} />
            </div>
            <div>
              <label className={lbl}>Símbolo Coinbase</label>
              <input value={form.geckoId} onChange={(e) => set({ geckoId: e.target.value.toLowerCase() })} placeholder="avalanche-2" className={inp} />
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Quantidade</label>
            <input value={form.qtd} onChange={(e) => set({ qtd: e.target.value })} inputMode="decimal" placeholder="0.5" className={inp} />
          </div>
          <div>
            <label className={lbl}>Preço médio (R$)</label>
            <input value={form.pmc} onChange={(e) => set({ pmc: e.target.value })} inputMode="decimal" placeholder="300000" className={inp} />
          </div>
        </div>
        <div>
          <label className={lbl}>Data de compra <span className="text-rose-400">*</span></label>
          <input
            type="date"
            value={form.dataCompra}
            onChange={(e) => set({ dataCompra: e.target.value })}
            className={inp}
          />
        </div>
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="flex-1 h-10 rounded-xl border border-slate-700 text-sm text-slate-300 hover:bg-slate-800 transition">Cancelar</button>
          <button type="submit" className="flex-1 h-10 rounded-xl monyx-btn-primary text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition">
            <Save className="h-4 w-4" /> Salvar
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

// ─── CDI / Renda Fixa Modal ────────────────────────────────────
export const CdiModal = ({ open, onClose }) => {
  const { addCdi } = useAppData();
  const EMPTY = { data: new Date().toISOString().slice(0,10), banco: "", ativo: "", tipo: "CDB", valor: "", cdiAn: 13.75, pctCdi: 100, dias: 0, taxa: "100% CDI", rendimento: 0, valorRef: 0 };
  const [form, setForm] = useState(EMPTY);
  useEffect(() => { if (open) setForm(EMPTY); }, [open]); // eslint-disable-line

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const n   = (v) => Number(String(v).replace(",", "."));

  const submit = (e) => {
    e.preventDefault();
    if (!form.banco.trim() || !form.ativo.trim()) return toast.error("Preencha banco e ativo.");
    const valor = n(form.valor);
    if (!valor) return toast.error("Valor inválido.");
    addCdi({ ...form, valor, valorRef: valor, rendimento: n(form.rendimento) || 0 });
    toast.success("Renda fixa adicionada!");
    onClose();
  };

  if (!open) return null;
  return (
    <ModalShell title="Nova renda fixa" subtitle="CDB, LCI, Tesouro, RDB e similares" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Data</label>
            <input type="date" value={form.data} onChange={(e) => set({ data: e.target.value })} className={inp} />
          </div>
          <div>
            <label className={lbl}>Tipo</label>
            <select value={form.tipo} onChange={(e) => set({ tipo: e.target.value })} className={sel}>
              {TIPOS_CDI.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Banco/Corretora</label>
            <input value={form.banco} onChange={(e) => set({ banco: e.target.value })} placeholder="Itaú, XP..." className={inp} />
          </div>
          <div>
            <label className={lbl}>Nome do ativo</label>
            <input value={form.ativo} onChange={(e) => set({ ativo: e.target.value })} placeholder="CDB DI 110%" className={inp} />
          </div>
          <div>
            <label className={lbl}>Valor (R$)</label>
            <input value={form.valor} onChange={(e) => set({ valor: e.target.value })} inputMode="decimal" placeholder="10000" className={inp} />
          </div>
          <div>
            <label className={lbl}>% CDI</label>
            <input value={form.pctCdi} onChange={(e) => set({ pctCdi: Number(e.target.value) })} inputMode="numeric" className={inp} />
          </div>
          <div className="col-span-2">
            <label className={lbl}>Taxa</label>
            <input value={form.taxa} onChange={(e) => set({ taxa: e.target.value })} placeholder="100% CDI" className={inp} />
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="flex-1 h-10 rounded-xl border border-slate-700 text-sm text-slate-300 hover:bg-slate-800 transition">Cancelar</button>
          <button type="submit" className="flex-1 h-10 rounded-xl monyx-btn-primary text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition">
            <Save className="h-4 w-4" /> Salvar
          </button>
        </div>
      </form>
    </ModalShell>
  );
};
